import { Injectable } from '@angular/core';
import * as d3 from 'd3';
import { Arc, DefaultArcObject } from 'd3';

/** Interface with d3 graphin library */
@Injectable({
  providedIn: 'root',
})
export class GraphsService {
  constructor() {}
  /**
   * Create a pie chart
   * @param hostElement HTML element to add the chart
   * @param values Value for driving the chart
   * @param settings Settings for the chart
   */
  generatePie(
    hostElement: HTMLElement | any,
    values: Array<any>,
    settings
  ): Pie {
    const pie = new Pie();
    pie.init(hostElement, values, settings);
    return pie;
  }
}

export class Pie {
  constructor() {}

  data: Array<any>;
  /** Native element hosting the SVG container */
  hostElement: HTMLElement;
  /** Top level SVG element*/
  svg;
  /*  SVG Group element */
  g;
  /*  D3 Arc generator */
  arc;
  /** D3 outer arc*/
  outerArc;
  /*  Inner radius of donut chart */
  innerRadius: number;
  /*  Outer radius of donut chart */
  radius: number;
  /*  Donut chart slice elements */
  slices;
  /*  SVG data label elements */
  labels;
  /*  Label lines elements */
  lines;
  /*  SVG label for total */
  totalLabel;
  /*  Raw chart values array */
  rawData;
  /*  Total of chart values */
  total: number;
  /*  D3 color provider */
  colorScale;
  /*  Arc segment parameters for current data set */
  pieData: any;
  /*  Arc segment parameters for previous data set - used for transitions */
  pieDataPrevious: any;
  /** Color scheme*/
  colors = d3.scaleOrdinal(d3.schemeCategory10);
  /** Height of chart in px*/
  height = 120;
  /** Width of chart in px*/
  width = 200;
  /*  Pie function - transforms raw data to arc segment parameters */
  pie = d3
    .pie()
    /*.startAngle(-0.5 * Math.PI)
    .endAngle(0.5 * Math.PI)*/
    .sort(null)
    .value((d: number) => d);

  set values(val: Array<any>) {
    this.update(val);
  }
  /** Settings item*/
  item;
  init(hostElement: HTMLElement, values: Array<any>, settings) {
    this.hostElement = hostElement;
    this.item = settings;
    this.values = values;
  }
  /**
   * Update the chart
   * @param values Values for update
   */
  update(values) {
    this.updateChart(values);
  }
  /**
   * Create a pie chart
   * @param data Data to use for generating a new chart
   */
  private createChart(data: number[]) {
    this.processPieData(data);

    this.removeExistingChartFromParent();

    this.setChartDimensions();

    this.setColorScale();

    this.addGraphicsElement();

    this.setupArcGenerator();

    this.addSlicesToTheDonut();

    this.addLabelsToTheDonut();

    this.addLinesToTheDonut();
    if (this.item.showTotal) {
      this.addDonutTotalLabel();
    }
  }

  /** Set dimentions of the chart */
  private setChartDimensions() {
    this.svg = d3
      .select(this.hostElement)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', '0 0 ' + this.width + ' ' + this.height);
  }

  /** Add a visible element to the chart */
  private addGraphicsElement() {
    this.g = this.svg.append('g').attr('transform', e => {
      return `translate(${this.width / 2},${this.height / 2})`;
    });
  }
  /** Set color scheme to use in the chart */
  private setColorScale() {
    this.colorScale = d3.scaleOrdinal(d3.schemeCategory10);
    console.log('COLOR SCALE', this.colorScale);
    // Below is an example of using custom colors
    // this.colorScale = d3.scaleOrdinal().domain(["0","1","2","3"]).range(['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728']);
  }

  /**
   * Process data to be used in the chart
   * @param data Data to be processed
   * @param initial Is this the first render of the chart
   */
  private processPieData(data, initial = true) {
    this.rawData = data;
    this.total = this.rawData.reduce((sum, next) => sum + next, 0);

    this.pieData = this.pie(data);
    if (initial) {
      this.pieDataPrevious = this.pieData;
    }
  }

  /** Create the generator for drawing the pie chart shape*/
  private setupArcGenerator() {
    this.radius = Math.min(this.height, this.width) / 2;
    this.innerRadius = this.item.innerRadius
      ? this.radius * (this.item.innerRadius / 100)
      : 0;
    this.arc = d3
      .arc()
      .innerRadius(this.innerRadius)
      .outerRadius(this.radius * 0.8);
    this.outerArc = d3
      .arc()
      .innerRadius(this.radius * 0.9)
      .outerRadius(this.radius * 0.9);
  }
  /**Create the section of the pie chart */
  private addSlicesToTheDonut() {
    this.slices = this.g
      .selectAll('allSlices')
      .data(this.pieData)
      .enter()
      .append('path')
      .attr('d', this.arc)
      .attr('fill', (datum, index) => {
        if (
          this.item.data_items &&
          this.item.data_items[index] &&
          this.item.data_items[index].color
        ) {
          // infinate wrap the array index
          return this.item.data_items[index].color;
        } else {
          return this.colorScale(`${index}`);
        }
      })
      .style('opacity', 1);
  }
  /** Add a total label to the pie chart */
  private addDonutTotalLabel() {
    this.totalLabel = this.svg
      .append('text')
      .text(this.total)
      .attr('id', 'total')
      .attr('x', this.width / 2)
      .attr('y', this.height / 2 + 3)
      .style('font-size', '10px')
      .style('text-anchor', 'middle');
  }

  /* Creates an "interpolator" for animated transition for arc slices
   *   given previous and new arc shapes,
   *   generates a series of arc shapes (be)tween start and end state
   */
  arcTween = (datum, index) => {
    const interpolation = d3.interpolate(this.pieDataPrevious[index], datum);
    this.pieDataPrevious[index] = interpolation(0);
    return t => {
      return this.arc(interpolation(t));
    };
  };

  // Creates an "interpolator" for animated transition for arc labels
  //   given previous and new label positions,
  //   generates a series of arc states (be)tween start and end state
  labelTween = (datum, index) => {
    const interpolation = d3.interpolate(this.pieDataPrevious[index], datum);
    this.pieDataPrevious[index] = interpolation(0);
    return t => {
      return 'translate(' + this.arc(interpolation(t)) + ')';
    };
  };
  /**
   *  Update the chart with ne data
   * @param data New data to display
   */
  public updateChart(data: number[]) {
    if (!this.svg) {
      this.createChart(data);
      return;
    }

    this.processPieData(data, false);

    this.updateSlices();

    this.updateLabels();
  }
  /** Redraw slices */
  private updateSlices() {
    this.slices = this.slices.data(this.pieData);
    this.slices.transition().duration(750).attrTween('d', this.arcTween);
  }
  /**Redraw labels */
  private updateLabels() {
    this.totalLabel.text(this.total);
    this.labels.data(this.pieData);
    this.labels.each((datum, index, n) => {
      d3.select(n[index]).text(this.labelValueFn(this.rawData[index]));
    });
    this.labels
      .transition()
      .duration(750)
      .attrTween('transform', this.labelTween);
  }
  /** Redraw totla label */
  private updateTotal() {
    this.totalLabel.text(this.total);
  }
  /** Remove this specific d3 chart from the DOM */
  private removeExistingChartFromParent() {
    // !!!!Caution!!!
    // Make sure not to do;
    //     d3.select('svg').remove();
    // That will clear all other SVG elements in the DOM
    d3.select(this.hostElement).select('svg').remove();
  }
  /** Add section labels to the chart */
  private addLabelsToTheDonut() {
    let pos;
    this.labels = this.g
      .selectAll('allLabels')
      .data(this.pieData)
      .enter()
      .append('text')
      .text(this.labelValueGetter)
      .attr('transform', (datum, index) => {
        console.log(datum);
        pos = this.outerArc.centroid(datum);
        pos[0] =
          this.radius *
          (datum.startAngle + (datum.endAngle - datum.startAngle) / 2 < Math.PI
            ? 1
            : -1);
        pos[1] = pos[1];
        return 'translate(' + pos + ')';
      })
      .style('font-size', '3px')
      .style('text-anchor', function (d) {
        console.log(d);
        return d.startAngle + (d.endAngle - d.startAngle) / 2 < Math.PI
          ? 'start'
          : 'end';
      })
      .call(this.wrap, this.width / 2 - this.radius);
  }

  /**
   * Prtogramaticly line wrap lables in the svg based on available width
   * @param text Text of the label
   * @param width Width of the space for the label
   */
  wrap(text, width) {
    console.log(text);
    console.log('Width:', width);
    text.each(function () {
      const labelText = d3.select(this),
        words = labelText.text().split(/\s+/).reverse(),
        lineHeight = 0.1,
        y = labelText.attr('y'),
        dy = 0; // ems
      let word,
        line = [],
        lineNumber = 0,
        tspan = labelText
          .text(null)
          .append('tspan')
          .attr('x', 0)
          .attr('y', y)
          .attr('dy', dy);
      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(' '));
        console.log(tspan.node().getComputedTextLength());
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));
          line = [word];
          tspan = labelText
            .append('tspan')
            .attr('x', 0)
            .attr('y', y)
            .attr('dy', ++lineNumber * lineHeight + 1 + 'em')
            .text(word);
        }
      }
    });
  }
  /**Add the trace lines from label to chart section */
  private addLinesToTheDonut() {
    this.labels = this.g
      .selectAll('polyline')
      .data(this.pieData)
      .enter()
      .append('polyline')
      .attr('stroke', '#333')
      .attr('fill', 'none')
      .attr('stroke-width', '0.5')
      .attr('points', (datum, index) => {
        const pos2 = this.outerArc.centroid(datum);
        const pos = this.outerArc.centroid(datum);
        pos2[0] =
          this.radius *
          0.95 *
          (datum.startAngle + (datum.endAngle - datum.startAngle) / 2 < Math.PI
            ? 1
            : -1);
        pos[1] = pos[1] - 1;
        pos2[1] = pos2[1] - 1;
        return [this.arc.centroid(datum), pos, pos2];
      })
      .exit()
      .remove();
  }
  /** Format lables based on component settings*/
  private labelValueGetter = (datum, index) => {
    let v = { value: '0', label: '' };
    if (this.item && this.item.data_items) {
      v = this.item.data_items[index];
    }
    let label = v.label;

    if (this.item.value_display) {
      const val = Number(v.value);
      label += ' ';
      if (this.item.value_display === 'percent') {
        label += Math.floor((val * 100) / this.total) + '%';
      }
      if (this.item.value_display === 'value') {
        label += val;
      }
    }
    return label;

    // return this.labelValueFn(this.rawData[index]);
  };
  /**
   * Conver value to percent string
   * @param val Numeric value to convert
   */
  private labelValueFn(val: number): string {
    const pct = Math.floor((val * 100) / this.total);
    return pct < 4 ? '' : '' + val;
  }
}
